import stringify from 'json-stringify-safe';
import { LogLevel } from '../types/LogLevel.es.js';
import { filterErrorFields, filterUnallowedExtraInfosFields } from '../utils.es.js';

/**
 * Replaces LogService & LogglyService for Landr Clouds Apps
 * sending their logs to the STDOUT instead of sending them directly to loggly (BBFs, Lambdas...)
 */
class ConsoleLogService {
    constructor(className, logConfig, version) {
        var _a;
        this.className = className;
        this.logConfig = logConfig;
        this.version = version;
        this.logFunctions = {};
        logConfig.stringify = (_a = logConfig.stringify) !== null && _a !== void 0 ? _a : true;
    }
    getInstance(className, logConfig, version) {
        const newClassName = this.className ? `${this.className}${logConfig.separator}${className}` : className;
        return new ConsoleLogService(newClassName, logConfig, version);
    }
    log(message, eventId, extraArguments) {
        this._log(LogLevel.LOG, message, eventId, extraArguments);
    }
    debug(message, eventId, extraArguments) {
        this._log(LogLevel.DEBUG, message, eventId, extraArguments);
    }
    info(message, eventId, extraArguments) {
        this._log(LogLevel.INFO, message, eventId, extraArguments);
    }
    warn(message, eventId, error, extraArguments) {
        this._log(LogLevel.WARN, message, eventId, extraArguments, error);
    }
    error(message, eventId, error, extraArguments) {
        this._log(LogLevel.ERROR, message, eventId, extraArguments, error);
    }
    fatal(message, eventId, error, extraArguments) {
        this._log(LogLevel.FATAL, message, eventId, extraArguments, error);
    }
    isLevelEnabled(level) {
        return (this.logConfig.enabled &&
            Object.keys(LogLevel).indexOf(level) >= Object.keys(LogLevel).indexOf(this.logConfig.minLevel));
    }
    getLogFunction(level) {
        if (!this.logFunctions[level]) {
            // eslint-disable-next-line no-console
            this.logFunctions[level] = this.isLevelEnabled(level) ? console.log : () => { };
        }
        return this.logFunctions[level];
    }
    _log(level, message, eventId, extraArguments, error) {
        const log = this.buildLog(level, message, eventId, extraArguments, error);
        this.getLogFunction(level).apply(null, [log]);
    }
    buildLog(level, message, eventId, extraArguments, error) {
        const detailedError = error
            ? {
                error: filterErrorFields(error),
                errorRaw: typeof error !== 'string' ? stringify(error) : error,
            }
            : {};
        const extraInfos = {
            extraInfos: filterUnallowedExtraInfosFields(extraArguments),
            extraInfosRaw: extraArguments ? stringify(extraArguments) : undefined,
        };
        const extraInfosAllowedToRootLog = {
            userId: (extraArguments === null || extraArguments === void 0 ? void 0 : extraArguments.userId) || undefined,
            correlationId: (extraArguments === null || extraArguments === void 0 ? void 0 : extraArguments.correlationId) || undefined,
            consecutiveOccurrences: (extraArguments === null || extraArguments === void 0 ? void 0 : extraArguments.consecutiveOccurrences) || 0,
        };
        /**
         * Make sure it returns a LogglyStructuredEvent so both logging stacks stay consistent
         */
        const log = Object.assign(Object.assign(Object.assign(Object.assign({ level,
            eventId, message: this.formatMessage(message) }, extraInfosAllowedToRootLog), extraInfos), detailedError), { timestamp: new Date().toUTCString(), serviceKey: this.logConfig.serviceKey, deploymentKind: this.logConfig.deploymentKind, version: this.version, application: this.logConfig.application });
        return this.logConfig.stringify ? JSON.stringify(log) : log;
    }
    formatMessage(message) {
        return [this.className, message].join(this.logConfig.separator);
    }
}

export { ConsoleLogService };
